/**
 * Theme manager — vanilla TS, sem dependências externas.
 *
 * Modes:
 *  - 'light' | 'dark'   — usuário fixou explicitamente
 *  - 'system'           — segue prefers-color-scheme dinamicamente
 *
 * Persistência: localStorage[STORAGE_KEY] guarda LITERALMENTE 'light' | 'dark' | 'system'.
 * No primeiro load sem valor salvo, default é 'system'.
 *
 * Aplicação no DOM: `<html data-theme="light|dark">` (sempre resolvido, nunca 'system'
 * no atributo — o atributo reflete o tema EFETIVO).
 *
 * Listeners: `onThemeChange(cb)` é disparado quando o tema EFETIVO muda — seja porque
 * o usuário trocou, ou porque está em 'system' e o OS mudou.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'qualiapps-theme';
const DOM_ATTR = 'data-theme';

const VALID_THEMES: readonly Theme[] = ['light', 'dark', 'system'] as const;

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (VALID_THEMES as readonly string[]).includes(value);
}

function readStored(): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isTheme(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

function writeStored(t: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // localStorage indisponível — preferência só vive na sessão atual
  }
}

let systemQuery: MediaQueryList | null = null;
function getSystemQuery(): MediaQueryList | null {
  if (systemQuery) return systemQuery;
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  systemQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return systemQuery;
}

/**
 * Resolve um Theme para o tema EFETIVO ('light' | 'dark').
 * 'system' lê prefers-color-scheme; light/dark passa direto.
 */
export function resolveTheme(t: Theme): ResolvedTheme {
  if (t === 'light' || t === 'dark') return t;
  const q = getSystemQuery();
  return q && q.matches ? 'dark' : 'light';
}

/**
 * Aplica um Theme no DOM sem persistir.
 * Útil para previews ou para o inline-init script.
 */
export function applyTheme(t: Theme): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(t);
  document.documentElement.setAttribute(DOM_ATTR, resolved);
}

/**
 * Lê a preferência salva.
 * Retorna 'system' se nada estiver salvo ou se o valor for inválido.
 */
export function getTheme(): Theme {
  return readStored();
}

/**
 * Persiste + aplica.
 * `setTheme('system')` guarda literalmente 'system' (não resolve antes).
 */
export function setTheme(t: Theme): void {
  if (!isTheme(t)) return;
  const prevResolved = resolveTheme(readStored());
  writeStored(t);
  applyTheme(t);
  const nextResolved = resolveTheme(t);
  lastResolved = nextResolved;
  if (prevResolved !== nextResolved) {
    notify(nextResolved);
  }
}

/**
 * Avança o ciclo: light → dark → system → light.
 * Retorna o novo Theme persistido.
 */
export function cycleTheme(): Theme {
  const order: Theme[] = ['light', 'dark', 'system'];
  const current = readStored();
  const next = order[(order.indexOf(current) + 1) % order.length] ?? 'system';
  setTheme(next);
  return next;
}

// ---------- listeners ----------

type Listener = (resolved: ResolvedTheme) => void;
const listeners = new Set<Listener>();

function notify(resolved: ResolvedTheme): void {
  for (const cb of listeners) {
    try {
      cb(resolved);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[theme] listener threw', err);
    }
  }
}

let systemListenerAttached = false;
let lastResolved: ResolvedTheme | null = null;
function ensureSystemListener(): void {
  if (systemListenerAttached) return;
  const q = getSystemQuery();
  if (!q) return;
  const handler = (): void => {
    const stored = readStored();
    if (stored !== 'system') return; // só importa quando estamos em modo system
    const resolved = resolveTheme(stored);
    if (resolved === lastResolved) return;
    lastResolved = resolved;
    applyTheme(stored);
    notify(resolved);
  };
  q.addEventListener('change', handler);
  systemListenerAttached = true;
}

/**
 * Registra um callback que dispara sempre que o tema EFETIVO muda.
 * Retorna função de cleanup que remove o listener.
 */
export function onThemeChange(cb: Listener): () => void {
  ensureSystemListener();
  listeners.add(cb);
  if (lastResolved === null) {
    lastResolved = resolveTheme(readStored());
  }
  return () => {
    listeners.delete(cb);
  };
}

/**
 * Boot: aplica o tema salvo (ou system default) ao DOM e ativa o listener de system.
 * Idempotente — pode ser chamado várias vezes.
 */
export function initTheme(): void {
  const t = readStored();
  applyTheme(t);
  lastResolved = resolveTheme(t);
  ensureSystemListener();
}
